import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

/**
 * Phone only: patches Leaflet's getSize() so it returns a height reduced by the bottom card deck.
 * Every Leaflet operation (fitBounds, flyTo centering, getBoundsZoom) then automatically frames
 * content in the visible strip above the cards — no per-call padding hacks or post-move nudges.
 *
 * Returns a real L.Point so all internal Leaflet methods (divideBy, _divideBy, subtract, etc.) work.
 *
 * Do not call invalidateSize after the map panes are gone (e.g. navigating Map → Ranking on phone):
 * Leaflet throws TypeError reading `_leaflet_pos` and the CRA overlay can leave the UI stuck until refresh.
 */
function mapPanesAlive(map) {
  try {
    if (!map || map._removed) return false;
    const pane = map._mapPane;
    if (!pane || !pane.parentNode) return false;
    const container = typeof map.getContainer === "function" ? map.getContainer() : null;
    if (!container || !container.parentNode) return false;
    return true;
  } catch {
    return false;
  }
}

function safeInvalidateSize(map) {
  if (!mapPanesAlive(map)) return;
  try {
    map.invalidateSize({ animate: false });
  } catch {
    /* tear-down race — ignore */
  }
}

export default function PhoneMapViewportClamp() {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;

    const original = map.getSize.bind(map);

    function getDeckHeight() {
      if (typeof window === "undefined") return 240;
      const raw = getComputedStyle(document.documentElement)
        .getPropertyValue("--v2-mobile-deck-clearance")
        .trim();
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : 240;
    }

    map.getSize = function () {
      const real = original();
      const deckH = getDeckHeight();
      const clampedY = Math.max(100, real.y - deckH);
      return new L.Point(real.x, clampedY);
    };

    safeInvalidateSize(map);

    return () => {
      try {
        map.getSize = original;
      } catch {
        /* map already gone */
      }
      // Only resize if the map is still mounted (e.g. phone → desktop). Skip when leaving Map.
      safeInvalidateSize(map);
    };
  }, [map]);

  return null;
}
